import { __assign, __rest } from "tslib";
import * as React from 'react';
import { default as MuiCircularProgress, } from '@material-ui/core/CircularProgress';
import clsx from 'clsx';
import { Typography } from '../../Typography';
import { getColor, makeStyles, createStyles } from '../../styles';
import { Check } from '../../../internal/icons/small/Check';
import { Exclamation } from '../../../internal/icons/medium/Exclamation';
var CircularProgressSize;
(function (CircularProgressSize) {
    CircularProgressSize[CircularProgressSize["small"] = 24] = "small";
    CircularProgressSize[CircularProgressSize["medium"] = 38] = "medium";
    CircularProgressSize[CircularProgressSize["large"] = 50] = "large";
    CircularProgressSize[CircularProgressSize["xlarge"] = 76] = "xlarge";
})(CircularProgressSize || (CircularProgressSize = {}));
var CircularProgressVariant;
(function (CircularProgressVariant) {
    CircularProgressVariant["determinate"] = "determinate";
    CircularProgressVariant["indeterminate"] = "indeterminate";
    CircularProgressVariant["static"] = "static";
})(CircularProgressVariant || (CircularProgressVariant = {}));
export var useStyles = makeStyles(function (theme) {
    var progressBorder = theme.palette.type === 'light'
        ? getColor('border.dark', theme)
        : getColor('secondary.light', theme);
    return createStyles({
        root: {
            display: 'inline-flex',
            flexDirection: 'column',
            alignItems: 'center',
        },
        secondaryRoot: {
            display: 'inline-flex',
            alignItems: 'center',
        },
        smallSize: {
            width: CircularProgressSize.small,
            height: CircularProgressSize.small,
        },
        mediumSize: {
            width: CircularProgressSize.medium,
            height: CircularProgressSize.medium,
        },
        largeSize: {
            width: CircularProgressSize.large,
            height: CircularProgressSize.large,
        },
        xlargeSize: {
            width: CircularProgressSize.xlarge,
            height: CircularProgressSize.xlarge,
        },
        smallLabel: {
            lineHeight: 1.5,
            fontSize: theme.typography.fontSizeDefault,
            fontWeight: theme.typography.fontWeightSemiBold,
            paddingTop: '12px',
        },
        mediumLabel: {
            lineHeight: 1.5,
            fontSize: theme.typography.fontSizeDefault,
            fontWeight: theme.typography.fontWeightSemiBold,
            paddingTop: '12px',
        },
        largeLabel: {
            lineHeight: 1.5,
            fontSize: theme.typography.fontSizeMedium,
            fontWeight: theme.typography.fontWeightSemiBold,
            paddingTop: '12px',
        },
        xlargeLabel: {
            lineHeight: 1.5,
            fontSize: theme.typography.fontSizeLarge,
            fontWeight: theme.typography.fontWeightRegular,
            paddingTop: '12px',
        },
        circleContainer: {
            position: 'relative',
        },
        backgroundCircle: {
            position: 'absolute',
        },
        circleOutline: {
            stroke: progressBorder,
        },
        circleActive: {
            color: getColor('brand.main', theme),
            position: 'relative',
            left: 0,
            strokeLinecap: 'round',
        },
        errorState: {
            color: theme.palette.error.main,
            position: 'relative',
            left: 0,
        },
        successState: {
            color: theme.palette.success.main,
            position: 'relative',
            left: 0,
        },
        progressValue: {
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
        },
        valueTextMedium: {
            fontSize: theme.typography.fontSizeXSmall,
            fontWeight: theme.typography.fontWeightSemiBold,
        },
        valueTextLarge: {
            fontSize: theme.typography.fontSizeSmall,
            fontWeight: theme.typography.fontWeightSemiBold,
        },
        valueTextXlarge: {
            fontSize: theme.typography.fontSizeDefault,
            fontWeight: theme.typography.fontWeightSemiBold,
        },
        valueWrapper: {
            position: 'relative',
            display: 'inline-flex',
        },
        svg: {
            transformOrigin: 'center',
            borderRadius: '100%',
            animation: '$progress-rotate 4s linear 0s infinite',
        },
        indeterminateSvg: {
            /**
             * Note: the use of color + stroke is intentional.
             * When stroke inherits from color (via currentColor), it allows the browser
             * to correctly override our value when high contrast mode is enabled.
             */
            color: getColor('brand.main', theme),
            stroke: 'currentColor',
            strokeWidth: 16,
            strokeLinecap: 'round',
            strokeDasharray: '45px',
            transition: 'stroke 300ms',
        },
        '@keyframes progress-rotate': {
            '0%': {
                transform: 'rotate(0deg)',
            },
            '100%': {
                transform: 'rotate(360deg)',
            },
        },
    });
});
var getSize = function (size) {
    switch (size) {
        case 'small':
            return CircularProgressSize.small;
        case 'medium':
            return CircularProgressSize.medium;
        case 'large':
            return CircularProgressSize.large;
        case 'xlarge':
            return CircularProgressSize.xlarge;
        default:
            return +size;
    }
};
export var CircularProgress = function (props, ref) {
    var _a;
    var _b = props.error, error = _b === void 0 ? false : _b, _c = props.formatPercentage, formatPercentage = _c === void 0 ? function (value) { return "".concat(value, "%"); } : _c, id = props.id, minValue = props.minValue, maxValue = props.maxValue, _d = props.showPercent, showPercent = _d === void 0 ? false : _d, _e = props.size, size = _e === void 0 ? CircularProgressSize.large : _e, _f = props.success, success = _f === void 0 ? false : _f, value = props.value, _g = props.variant, variant = _g === void 0 ? CircularProgressVariant.indeterminate : _g, className = props.className, withAriaProps = __rest(props, ["error", "formatPercentage", "id", "minValue", "maxValue", "showPercent", "size", "success", "value", "variant", "className"]);
    var _h = withAriaProps, ariaLabel = _h.ariaLabel, withLabelProps = __rest(_h, ["ariaLabel"]);
    var _j = withLabelProps, label = _j.label, labelProps = _j.labelProps, other = __rest(_j, ["label", "labelProps"]);
    var classes = useStyles(props);
    var spinnerSize = getSize(size);
    var sanitizedValue = value ? Math.min(100, Math.max(0, value || 0)) : value;
    var DEFAULT_THICKNESS = 4;
    if (label && !id) {
        throw new Error("The id must be supplied when using an on-screen label");
    }
    var backgroundCircle = (React.createElement("svg", { "aria-hidden": true, className: classes.backgroundCircle, viewBox: "0 0 100 100" },
        React.createElement("circle", { className: classes.circleOutline, cx: 50, cy: 50, r: 49.5, fill: "none" }),
        React.createElement("circle", { className: classes.circleOutline, cx: 50, cy: 50, r: 41, fill: "none" })));
    var renderProgressOrIcons = function () {
        var _a, _b, _c, _d;
        if (error || success) {
            return (React.createElement(React.Fragment, null,
                backgroundCircle,
                React.createElement("div", { className: classes.valueWrapper },
                    React.createElement(MuiCircularProgress, __assign({ thickness: DEFAULT_THICKNESS, size: spinnerSize, className: clsx(className, (_a = {},
                            _a[classes.errorState] = error,
                            _a[classes.successState] = success,
                            _a)), variant: CircularProgressVariant.static, value: error ? value : 100, "aria-valuenow": undefined, "aria-valuemax": undefined, "aria-valuemin": undefined, "aria-valuetext": undefined, role: error ? 'alert' : 'status', "aria-label": !label ? ariaLabel : undefined, "aria-labelledby": label ? "circular-progress-text-".concat(id) : undefined, ref: ref }, other)),
                    spinnerSize >= CircularProgressSize.medium ? (React.createElement("div", { className: classes.progressValue }, error ? React.createElement(Exclamation, null) : React.createElement(Check, null))) : null)));
        }
        if (variant === CircularProgressVariant.determinate && sanitizedValue !== undefined) {
            return (React.createElement(React.Fragment, null,
                showPercent && backgroundCircle,
                React.createElement("div", { className: classes.valueWrapper },
                    React.createElement(MuiCircularProgress, __assign({ variant: variant, "aria-label": !label ? ariaLabel : undefined, "aria-labelledby": label ? "circular-progress-text-".concat(id) : undefined, "aria-valuemin": minValue !== null && minValue !== void 0 ? minValue : 0, "aria-valuemax": maxValue !== null && maxValue !== void 0 ? maxValue : 100, "aria-valuetext": formatPercentage(sanitizedValue), className: clsx(className, classes.circleActive), size: spinnerSize, thickness: DEFAULT_THICKNESS, value: sanitizedValue, ref: ref }, other)),
                    spinnerSize >= CircularProgressSize.medium ? (React.createElement("div", { className: classes.progressValue },
                        React.createElement(Typography, { className: clsx(classes.root, (_b = {},
                                _b[classes.valueTextMedium] = spinnerSize > CircularProgressSize.small &&
                                    spinnerSize < CircularProgressSize.large,
                                _b[classes.valueTextLarge] = spinnerSize > CircularProgressSize.medium &&
                                    spinnerSize < CircularProgressSize.xlarge,
                                _b[classes.valueTextXlarge] = spinnerSize >= CircularProgressSize.xlarge,
                                _b)) }, formatPercentage(sanitizedValue)))) : null)));
        }
        if (variant === 'static' && sanitizedValue !== undefined) {
            return (React.createElement(React.Fragment, null,
                backgroundCircle,
                React.createElement("div", { className: classes.valueWrapper },
                    React.createElement(MuiCircularProgress, __assign({ variant: variant, "aria-valuemin": undefined, "aria-valuemax": undefined, "aria-valuetext": undefined, "aria-valuenow": undefined, role: undefined, "aria-hidden": true, className: classes.circleActive, size: spinnerSize, thickness: DEFAULT_THICKNESS, value: sanitizedValue, ref: ref }, other)),
                    showPercent && spinnerSize >= CircularProgressSize.medium ? (React.createElement("div", { className: classes.progressValue },
                        React.createElement(Typography, { className: clsx(classes.root, (_c = {},
                                _c[classes.valueTextMedium] = spinnerSize > CircularProgressSize.small &&
                                    spinnerSize < CircularProgressSize.large,
                                _c[classes.valueTextLarge] = spinnerSize > CircularProgressSize.medium &&
                                    spinnerSize < CircularProgressSize.xlarge,
                                _c[classes.valueTextXlarge] = spinnerSize >= CircularProgressSize.xlarge,
                                _c)) }, formatPercentage(sanitizedValue)))) : null)));
        }
        // Indeterminate variant return
        return (React.createElement("div", __assign({ className: clsx(className, classes.root, (_d = {},
                _d[classes.smallSize] = spinnerSize <= CircularProgressSize.small,
                _d[classes.mediumSize] = spinnerSize > CircularProgressSize.small && spinnerSize < CircularProgressSize.large,
                _d[classes.largeSize] = spinnerSize > CircularProgressSize.medium && spinnerSize < CircularProgressSize.xlarge,
                _d[classes.xlargeSize] = spinnerSize >= CircularProgressSize.xlarge,
                _d)), role: "status", "aria-label": !label ? ariaLabel : undefined, "aria-labelledby": label ? "circular-progress-text-".concat(id) : undefined, ref: ref }, other),
            React.createElement("svg", { className: classes.svg, viewBox: "-10 -10 220 220", focusable: "false", "aria-hidden": "true" },
                React.createElement("path", { className: classes.indeterminateSvg, d: "m100,0a100,100 0 0 1 0,200a100,100 0 0 1 0,-200", fill: "transparent", strokeDasharray: 629, strokeDashoffset: 629 }))));
    };
    return (React.createElement("div", { className: classes.root },
        React.createElement("div", { className: classes.circleContainer }, renderProgressOrIcons()),
        label ? (React.createElement(Typography, __assign({ component: "p", id: "circular-progress-text-".concat(id), variant: spinnerSize > CircularProgressSize.small ? 'h4' : 'h5', className: clsx(classes.root, (_a = {},
                _a[classes.smallLabel] = spinnerSize <= CircularProgressSize.small,
                _a[classes.mediumLabel] = spinnerSize > CircularProgressSize.small && spinnerSize < CircularProgressSize.large,
                _a[classes.largeLabel] = spinnerSize > CircularProgressSize.medium &&
                    spinnerSize < CircularProgressSize.xlarge,
                _a[classes.xlargeLabel] = spinnerSize >= CircularProgressSize.xlarge,
                _a)) }, labelProps), label)) : null));
};
var CircularProgressRef = React.forwardRef(CircularProgress);
export default CircularProgressRef;
//# sourceMappingURL=CircularProgress.js.map